import { Injectable, Inject } from '@angular/core';
import { AppSettingsService } from './app-settings.service';
import { AuthorizationService } from './authorization.service';
import { HttpClient } from '@angular/common/http';

export interface IAgentCertificateSettings {
    AgentCertificateExpiryDate: Date
}

export type AgentCertificateErrorTypes = string;
export const AgentCertificateErrorEnum =
{
    ERROR: 'ERROR',
    WARNING: 'WARNING',
}

export interface IAgentCertificateErrorExpire {
    message: string,
    messageType: AgentCertificateErrorTypes,
    expireDate: Date,
}

export interface IAgentCertificateError {
    message: string,
    messageType: AgentCertificateErrorTypes,
}

interface IStorageObject {
    watched: certificateWatchedState[]
}

interface certificateWatchedState {
    certificateKey: string,
    watchDate: Date,
    userId: string,
    isExpired: boolean
}

interface ISystemSettingsResponse {
    agentCertificateExpiryDate: string
    agentCertificateError: boolean
}

const STORAGE_OBJECT_KEY: string = "ls.certificateServiceState";
const DAYS_BEFORE_EXPIRY: number = 30;

export interface ICertificateService {
    checkCertificateExpire(): Promise<IAgentCertificateErrorExpire>;
    checkCertificateError(): Promise<IAgentCertificateError>;
}

/**
 * Certificate Service.
 * The certificate service provides the ability to work with agent certificate data.
 */
@Injectable({
    providedIn: 'root',
})

export class CertificateService implements ICertificateService {
    private requestCertificateSettings: Promise<Object> = undefined;

    constructor(
        private authorizationService: AuthorizationService,
        @Inject('LOCALSTORAGE')
        private localStorage: Storage,
        private $http: HttpClient,
        private appSettingsService: AppSettingsService,
    ) {
        const isAnyAdmin = this.authorizationService.isSystemAdmin() || this.authorizationService.isAnyGroupAdmin();
        if (isAnyAdmin) {
            this.requestCertificateSettings = this.$http.get(this.appSettingsService.systemServiceUrl + 'agent/config').toPromise();
        }
    }

    public checkCertificateExpire = (): Promise<IAgentCertificateErrorExpire> => {
        return new Promise<IAgentCertificateErrorExpire>((resolve, reject) => {

            if (this.requestCertificateSettings) {
                this.requestCertificateSettings.then((result: ISystemSettingsResponse) => {
                    const certificateKey = result.agentCertificateExpiryDate;
                    if (certificateKey == undefined) {
                        resolve(undefined);
                    }
                    else {
                        const isError = result.agentCertificateError;
                        const isSoonExpiring = this.isWarningDateExpiringAppropriate(certificateKey);
                        const isExpired = this.isWarningDateExpiredAppropriate(certificateKey);
                        const isWatched = this.isWatchedExpiry(certificateKey, isExpired);
                        if (!isError && !isWatched && (isSoonExpiring || isExpired)) {
                            resolve({
                                message: isSoonExpiring ? "error_CertificateExpiring" : "error_CertificateExpired",
                                messageType: AgentCertificateErrorEnum.ERROR,
                                expireDate: new Date(certificateKey)
                            });
                            this.setWatched(certificateKey, isExpired);
                        }
                    }
                }, (error) => {
                    reject(error);
                });
            }
        });
    }

    public checkCertificateError = (): Promise<IAgentCertificateError> => {
        return new Promise<IAgentCertificateError>((resolve, reject) => {
            const isAnyAdmin = this.authorizationService.isSystemAdmin() || this.authorizationService.isAnyGroupAdmin();
            if (this.requestCertificateSettings) {
                this.requestCertificateSettings.then((result: ISystemSettingsResponse) => {
                    const isError = result.agentCertificateError;
                    const isWatched = this.isWatchedError();
                    if (isError && isAnyAdmin && !isWatched) {
                        resolve({ message: "error_CertificateError", messageType: AgentCertificateErrorEnum.ERROR });
                        this.setWatched('');
                    }
                }, (error) => {
                    reject(error);
                });
            }
        });
    }

    private isWatchedExpiry = (certificateKey: string, isExpired: boolean): boolean => {
        const currentUserId = this.authorizationService.getUserId();
        const rawData = JSON.parse(this.localStorage.getItem(STORAGE_OBJECT_KEY)) as IStorageObject;
        let isWatched = false;
        if (rawData) {
            isWatched = rawData.watched.some((itemWatched: certificateWatchedState) => {
                return itemWatched.userId == currentUserId &&
                    itemWatched.certificateKey == certificateKey &&
                    itemWatched.isExpired == isExpired;
            })
        }
        return isWatched;
    }

    private isWatchedError = (): boolean => {
        const currentUserId = this.authorizationService.getUserId();
        const currentDate = new Date();
        const rawData = JSON.parse(this.localStorage.getItem(STORAGE_OBJECT_KEY)) as IStorageObject;
        let isWatched = false;
        if (rawData) {
            isWatched = rawData.watched.some((itemWatched: certificateWatchedState) => {
                const itemWatchDate = new Date(itemWatched.watchDate);
                return itemWatched.userId == currentUserId
                    && itemWatched.certificateKey == ''
                    && itemWatched.watchDate
                    && itemWatchDate.getUTCFullYear() == currentDate.getUTCFullYear()
                    && itemWatchDate.getUTCMonth() == currentDate.getUTCMonth()
                    && itemWatchDate.getUTCDay() == currentDate.getUTCDay();
            })
        }
        return isWatched;
    }

    private setWatched = (certificateKey: string, isExpired?: boolean): void => {
        let rawData = JSON.parse(this.localStorage.getItem(STORAGE_OBJECT_KEY)) as IStorageObject;
        if (rawData && rawData.watched && Array.isArray(rawData.watched)) {
            rawData.watched.push(this.getNewWatched(certificateKey, isExpired));
        }
        else {
            rawData = { watched: [this.getNewWatched(certificateKey, isExpired)] } as IStorageObject;
        }
        this.localStorage.setItem(STORAGE_OBJECT_KEY, JSON.stringify(rawData));
    }

    private getNewWatched = (certificateKey: string, isExpired?: boolean): certificateWatchedState => {
        const currentUserId = this.authorizationService.getUserId();
        return {
            certificateKey: certificateKey,
            watchDate: new Date(),
            userId: currentUserId,
            isExpired: isExpired
        } as certificateWatchedState
    }

    private isWarningDateExpiringAppropriate = (certificateKey: string) => {
        const days = this.getDaysBeforeExpiry(certificateKey);
        return days && (days >= 0) && (days < DAYS_BEFORE_EXPIRY)
    }

    private isWarningDateExpiredAppropriate = (certificateKey: string) => {
        const days = this.getDaysBeforeExpiry(certificateKey);
        return days && (days < 0) && (days < DAYS_BEFORE_EXPIRY)
    }

    private getDaysBeforeExpiry = (certificateKey: string) => {
        let dayUntilExpire = undefined;
        const certificateKeyDate = new Date(certificateKey)
        if (certificateKeyDate instanceof Date && !isNaN(certificateKeyDate.getMonth())) {
            dayUntilExpire = (certificateKeyDate.valueOf() - new Date().valueOf()) / (24 * 60 * 60 * 1000)
        }
        return dayUntilExpire;
    }
}